import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  blurEditor,
  deselect,
  moveNodes,
} from 'lib-knowledge-document-structure/plate/common';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import { useMemo, useState } from 'react';

type DragManagerProps = {
  children?: React.ReactNode;
};
export const DragManager = ({ children }: DragManagerProps): JSX.Element => {
  const editor = useMyEditorRef();
  const [draggingNodeId, setDraggingNodeId] = useState<string | number | null>(
    null,
  );

  const handleDragStart = (e: DragStartEvent): void => {
    clearSelection();
    setDraggingNodeId(e.active.id);
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const overId = e.over?.id;
    const overIndex = editor.children.findIndex(
      (element) => element.id === overId,
    );

    if (overId !== draggingNodeId && overIndex !== -1) {
      moveNodes(editor, {
        at: [],
        match: (node) => 'id' in node && node.id === draggingNodeId,
        to: [overIndex],
      });
    }

    setDraggingNodeId(null);
  };

  const handleDragCancel = () => setDraggingNodeId(null);

  const clearSelection = () => {
    blurEditor(editor);
    deselect(editor);
    window.getSelection()?.empty();
  };

  const items = useMemo(
    () => editor.children.map((element) => element.id), // All first level node are of type Element
    [editor.children],
  );

  // see: https://docs.dndkit.com/api-documentation/sensors/pointer
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5,
    },
  });

  const sensors = useSensors(pointerSensor);

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>

      {/* Snaps the dragging block */}
      <DragOverlay />
    </DndContext>
  );
};
